/* eslint-disable */
// @ts-nocheck
import {
  Button,
  Text,
  TextField,
  Dialog,
  Flex,
  Spinner,
} from "@radix-ui/themes";
import { createRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { ArrowRight, X, WarningCircle } from "@phosphor-icons/react";

import { processRoute } from "./process";

import { useCreateSession } from "../../utils/queries/brandcheck";
import { rootRoute } from "../root/route";
import aarpLogo from "../../images/brands/aarp.png";
import nicoretteLogo from "../../images/brands/nicorette.png";
import neutrogenaLogo from "../../images/brands/neutrogena.png";
import bandaidLogo from "../../images/brands/bandaid.png";
import johnsonjohnsonLogo from "../../images/brands/johnsonjohnson.png";
import listerineLogo from "../../images/brands/listerine.png";
import aveenoLogo from "../../images/brands/aveeno.png";
import kenvueLogo from "../../images/brands/kenvue.png";
import enverusLogo from "../../images/brands/enverus.png";

// PUNTT logo component
export const PunttLogo = () => (
  <svg
    width="200"
    height="45"
    viewBox="0 0 200 45"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-labelledby="puntt-logo-title"
    role="img"
  >
    <title id="puntt-logo-title">Puntt Logo</title>
    <path
      d="M114.055 20.1028V0.000976562H129.018V43.4575H115.161L101.825 23.4858H101.695V43.4575H86.7319V0.000976562H101.304L113.925 20.1028H114.055Z"
      fill="#3321DA"
    />
    <path
      d="M62.2271 31.2271C66.1954 31.2271 66.6508 27.7793 66.6508 24.6567V0.000976562H82.3943V25.6325C82.3943 39.0987 73.8069 44.2382 62.2271 44.2382C50.6474 44.2382 42.06 39.0987 42.06 25.6325V0.000976562H57.8035V24.6567C57.8035 27.7793 58.2589 31.2271 62.2271 31.2271Z"
      fill="#3321DA"
    />
    <path
      d="M21.143 32.3971H15.7435V43.4565H0V39.8718C0.86988 25.5979 6.82534 20.6789 15.105 20.4439L19.1913 20.4271C21.143 20.4271 22.9645 19.4513 22.9645 16.3936C22.9645 13.5314 21.2079 12.2952 19.1913 12.2952C12.7945 12.2921 6.39606 12.2722 0 12.384V0H21.143C31.8771 0 38.9679 5.46441 38.9679 16.3287C38.9679 26.8678 30.7711 32.3971 21.143 32.3971Z"
      fill="#3321DA"
    />
    <path
      d="M144.966 43.4575H160.71V12.8819H200V0.000976562H132.28V12.8819H144.966V43.4575Z"
      fill="#3321DA"
    />
    <path
      d="M189.191 43.4571H173.459C173.508 26.0837 179.977 20.4836 189.191 20.4355V43.4571Z"
      fill="#3321DA"
    />
  </svg>
);

export const startRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/start",
  component: function StartComponent() {
    const [email, setEmail] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const [typedText, setTypedText] = useState("");
    const [isTypingComplete, setIsTypingComplete] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [errorInfo, setErrorInfo] = useState<{
      domain?: string;
      message: string;
      detail: string;
    } | null>(null);
    const hasText = email.trim().length > 0;
    const { mutateAsync } = useCreateSession();
    const navigate = useNavigate();

    const introText =
      "I'm Puntt. I'll scan your brand and show you real issues you can confirm in under 90 seconds.";

    // Typing animation effect
    useEffect(() => {
      let index = 0;
      const timer = setInterval(() => {
        if (index <= introText.length) {
          setTypedText(introText.slice(0, index));
          index++;
        } else {
          clearInterval(timer);
          setIsTypingComplete(true);
        }
      }, 30); // Adjust speed as needed

      return () => clearInterval(timer);
    }, []);

    const handleSubmit = async (e: React.FormEvent | React.KeyboardEvent) => {
      e.preventDefault();
      if (!email.trim()) return;

      try {
        const response = await mutateAsync({ email: email.trim() });

        // Success - navigate immediately without any modal
        navigate({
          to: processRoute.to,
          search: { sessionId: response.session_id },
        });
      } catch (error: any) {
        console.error("Error creating session:", error);

        // Only show modal for 404 errors (domain not supported)
        if (error?.status === 404 || error?.response?.status === 404) {
          // Show modal with processing animation first
          setShowModal(true);
          setIsProcessing(true);
          setErrorInfo(null);

          // Show processing animation for 5 seconds before showing error
          setTimeout(() => {
            setIsProcessing(false);

            // Extract domain from email for display
            const domain = email.includes("@") ? email.split("@")[1] : null;

            // Use backend error info if available, otherwise create our own
            const errorData = error?.response?.data || error?.data || {};
            setErrorInfo({
              domain: errorData.domain || domain,
              message:
                errorData.message || `We don't currently support ${domain}`,
              detail:
                errorData.detail ||
                `Brand analysis is not yet available for ${domain}. Please contact support if you'd like us to add support for your domain.`,
            });
          }, 5000);
        } else {
          // For other errors, just log them (or handle differently)
          console.error("Non-404 error:", error);
          // Could show a different error handling here if needed
        }
      }
    };

    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F2F2F0] p-4">
        <div className="bg-white w-full max-w-[1200px] overflow-hidden rounded-lg">
          <div className="p-6 text-center">
            <div className="mb-8 flex justify-center">
              <PunttLogo />
            </div>

            <h1 className="mb-8 text-center text-[60px] font-bold leading-[60px] tracking-[-0.4px] text-[#21201C]">
              <span className="whitespace-nowrap">
                Your marketing compliance
              </span>
              <br />
              review starts here.
            </h1>

            {/* Fixed height container to prevent layout shifts */}
            <div className="mb-1 flex h-[90px] items-start justify-center">
              <p className="max-w-[720px] text-center text-[24px] leading-[30px] tracking-[-0.1px] text-[#21201C]">
                {typedText}
                {!isTypingComplete && <span className="animate-pulse">|</span>}
              </p>
            </div>
          </div>

          <div className="px-6 pb-6">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="mb-20 space-y-2">
                <label
                  htmlFor="email"
                  className="mx-auto block w-[600px] text-left text-[16px] font-medium leading-[24px] text-[#21201C]"
                >
                  Your Company or Work Email
                </label>

                <div className="mx-auto flex w-[600px] items-center">
                  <div
                    className={`transition-all duration-300 ease-out ${hasText ? "w-[calc(600px-126px-16px)]" : "w-full"}`}
                  >
                    <TextField.Root
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Type your company name or work email"
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      onKeyDown={(e) => {
                        console.log(
                          "Key pressed:",
                          e.key,
                          "hasText:",
                          hasText,
                          "email:",
                          email,
                        );
                        if (e.key === "Enter" && hasText) {
                          console.log(
                            "Enter key triggered, calling handleSubmit",
                          );
                          e.preventDefault();
                          handleSubmit(e);
                        }
                      }}
                      className={`h-10 w-full transition-all duration-300 ease-out ${isFocused ? "ring-2 ring-[#3C39EC]/50" : ""} bg-white/90 rounded-lg border border-[rgba(18,18,1,0.114)] shadow-inner`}
                      required
                    />
                  </div>

                  {/* Gap moved to be before the button */}
                  <div
                    className={`transition-all duration-300 ease-out ${hasText ? "w-4" : "w-0"}`}
                  />

                  <div
                    className={`transition-all duration-300 ease-out ${hasText ? "w-auto translate-x-0 opacity-100" : "w-0 translate-x-4 overflow-hidden opacity-0"}`}
                  >
                    <Button
                      type="submit"
                      disabled={!hasText}
                      className={`h-10 w-[126px] px-4 transition-all duration-300 ease-out ${hasText ? "scale-100 opacity-100" : "scale-95 opacity-0"} text-white rounded-lg bg-[#3C39EC] font-medium hover:bg-[#2A28D4]`}
                      data-testid="lets-go-button"
                    >
                      <span
                        className="mr-2"
                        style={{
                          fontFamily: "Typography/Font family/sans",
                          fontWeight: 500,
                          fontStyle: "Medium",
                          fontSize: "Typography/Font size/3",
                          lineHeight: "Typography/Line height/3",
                          letterSpacing: "Typography/Letter spacing/3",
                          verticalAlign: "middle",
                        }}
                      >
                        Let&apos;s Go!
                      </span>
                      <ArrowRight
                        size={16}
                        style={{
                          fontFamily: "Typography/Font family/sans",
                          fontWeight: 500,
                          fontStyle: "Medium",
                          fontSize: "Typography/Font size/3",
                          lineHeight: "Typography/Line height/3",
                          letterSpacing: "Typography/Letter spacing/3",
                          verticalAlign: "middle",
                        }}
                      />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Trusted Brands Section */}
              <div className="mt-16">
                <div className="mb-4 text-center">
                  <p className="text-[16px] font-medium leading-[24px] tracking-[2px] text-[#080800A0]">
                    TRUSTED BY TOP BRANDS
                  </p>
                </div>

                <div className="flex min-w-full flex-nowrap items-center justify-center gap-8">
                  <div className="flex h-20 w-[120px] items-center justify-center">
                    <img
                      src={nicoretteLogo}
                      alt="NICORETTE"
                      className="h-auto max-h-5 w-auto max-w-[100px] object-contain"
                    />
                  </div>
                  <div className="flex h-20 w-[120px] items-center justify-center">
                    <img
                      src={neutrogenaLogo}
                      alt="NEUTROGENA"
                      className="h-auto max-h-5 w-auto max-w-[100px] object-contain"
                    />
                  </div>
                  <div className="flex h-20 w-[120px] items-center justify-center">
                    <img
                      src={bandaidLogo}
                      alt="BAND-AID"
                      className="h-auto max-h-5 w-auto max-w-[100px] object-contain"
                    />
                  </div>
                  <div className="flex h-20 w-[120px] items-center justify-center">
                    <img
                      src={johnsonjohnsonLogo}
                      alt="JOHNSON&JOHNSON"
                      className="h-auto max-h-5 w-auto max-w-[100px] object-contain"
                    />
                  </div>
                  <div className="flex h-20 w-[120px] items-center justify-center">
                    <img
                      src={listerineLogo}
                      alt="LISTERINE"
                      className="h-auto max-h-5 w-auto max-w-[100px] object-contain"
                    />
                  </div>
                  <div className="flex h-20 w-[120px] items-center justify-center">
                    <img
                      src={aveenoLogo}
                      alt="AVEENO"
                      className="h-auto max-h-5 w-auto max-w-[100px] object-contain"
                    />
                  </div>
                  <div className="flex h-20 w-[120px] items-center justify-center">
                    <img
                      src={kenvueLogo}
                      alt="KENVUE"
                      className="h-auto max-h-5 w-auto max-w-[100px] object-contain"
                    />
                  </div>
                  <div className="flex h-20 w-[120px] items-center justify-center">
                    <img
                      src={enverusLogo}
                      alt="ENVERUS"
                      className="h-auto max-h-5 w-auto max-w-[100px] object-contain"
                    />
                  </div>
                  <div className="flex h-20 w-[120px] items-center justify-center">
                    <img
                      src={aarpLogo}
                      alt="AARP"
                      className="h-auto max-h-5 w-auto max-w-[100px] object-contain"
                    />
                  </div>
                </div>
              </div>

              {/* Testimonial Section */}
              <div className="mx-auto mb-12 mt-2 w-[600px]">
                <div
                  className="rounded-[20px] p-6"
                  style={{
                    width: "600px",
                    height: "170px",
                    background:
                      "linear-gradient(115.55deg, #3321DA 0%, #FFA900 100%)",
                  }}
                >
                  <div>
                    <p
                      className="mb-6"
                      style={{
                        background: "#FFFFFF00",
                        color: "white",
                        fontSize: "18px",
                        lineHeight: "26px",
                        letterSpacing: "-0.04px",
                        fontWeight: "400",
                      }}
                    >
                      "We ran Puntt and it flagged something that could have
                      cost us millions. Took five minutes to fix."
                    </p>
                    <p
                      className="text-[14px] leading-[20px]"
                      style={{
                        color: "white",
                        fontSize: "14px",
                        lineHeight: "20px",
                      }}
                    >
                      Rusty Wright - VP of Marketing, Global CPG Brand
                    </p>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Processing/Error Modal */}
        <Dialog.Root open={showModal} onOpenChange={setShowModal}>
          <Dialog.Content
            style={{ maxWidth: 450 }}
            aria-describedby={
              isProcessing ? "processing-description" : "error-description"
            }
          >
            <Dialog.Title className="text-center">
              {isProcessing
                ? "Analyzing Your Domain..."
                : "Domain Not Supported"}
            </Dialog.Title>

            {!isProcessing && (
              <Dialog.Close>
                <Button
                  variant="ghost"
                  size="1"
                  style={{ position: "absolute", top: 8, right: 8 }}
                  aria-label="Close dialog"
                >
                  <X size={16} />
                </Button>
              </Dialog.Close>
            )}

            <div className="py-6">
              {isProcessing ? (
                <Flex direction="column" align="center" gap="4">
                  <div className="flex items-center justify-center">
                    <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#3C39EC]"></div>
                  </div>
                  <Text size="3" className="text-gray-600 text-center">
                    Checking if we support your domain...
                  </Text>
                  <Text
                    size="2"
                    className="text-gray-500 max-w-[350px] text-center"
                    id="processing-description"
                  >
                    This usually takes just a moment while we verify our
                    coverage.
                  </Text>
                </Flex>
              ) : errorInfo ? (
                <Flex direction="column" align="center" gap="4">
                  <div className="mb-2 flex items-center justify-center">
                    <div className="bg-orange-100 flex size-16 items-center justify-center rounded-full">
                      <WarningCircle size={32} className="text-orange-600" />
                    </div>
                  </div>

                  <Text size="4" weight="medium" className="text-center">
                    {errorInfo.message}
                  </Text>

                  <Text
                    size="3"
                    className="text-gray-600 max-w-[380px] text-center leading-relaxed"
                    id="error-description"
                  >
                    {errorInfo.detail}
                  </Text>

                  {errorInfo.domain && (
                    <div className="bg-gray-50 mt-2 rounded-md px-3 py-2">
                      <Text size="2" className="text-gray-500 font-mono">
                        Domain: {errorInfo.domain}
                      </Text>
                    </div>
                  )}

                  <Flex gap="3" mt="4">
                    <Dialog.Close>
                      <Button variant="soft" size="3">
                        Try Different Email
                      </Button>
                    </Dialog.Close>
                    <Button
                      size="3"
                      onClick={() => {
                        // Contact support action - could open email client or contact form
                        window.location.href =
                          "mailto:support@example.com?subject=Domain Support Request&body=Please add support for domain: " +
                          (errorInfo.domain || email);
                      }}
                    >
                      Contact Support
                    </Button>
                  </Flex>
                </Flex>
              ) : null}
            </div>
          </Dialog.Content>
        </Dialog.Root>
      </div>
    );
  },
});
